import styled from 'styled-components';
import { FormikErrors, FormikTouched, FormikValues } from 'formik';

import { FitFeedbackFormActions } from 'productTemplate/state/fitFeedbackFormReducer';
import { getTranslations } from 'shared/utils/getTranslations';
import { QuestionForm } from 'shared/components/QuestionForm';
import { QuestionFormType } from 'shared/enums/QuestionFormType';
import { SNEAKERS_FIT as SNEAKERS_FIT_DATA } from 'productTemplate/enums/fitFeedback';
import { t } from 'localization/utils/t';
import { transformSizeOptions } from 'productTemplate/utils/transformSizeOptionsForFitFeedbackForm';
import { useStoreSelector } from 'store/hooks/storeHooks';
import {
  useFitFeedbackFormDispatch,
  useFitFeedbackFormState,
} from 'productTemplate/providers/FitFeedbackProvider';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontMedium, variableFont } from 'styles/theme/text';

const TP = 'productTemplate.fitFeedback.FitFeedbackFormSneakers';

const sizeGridStyle = (columns: number) => ({
  display: 'grid',
  gridGap: '8px',
  gridTemplateColumns: `repeat(${columns}, 1fr)`,
});

const fitGridStyle = {
  display: 'grid',
  gridGap: '0px',
  gridTemplateColumns: 'repeat(2, 1fr)',
};

interface IFitFeedbackFormSneakers {
  errors: FormikErrors<{ [key: string]: string }>;
  setFieldValue: (key: string, val: string) => void;
  touched: FormikTouched<FormikValues>;
}

export const FitFeedbackFormSneakers = ({
  errors,
  setFieldValue,
  touched,
}: IFitFeedbackFormSneakers) => {
  const { sizeOptions } = useStoreSelector((state) => state.pdp.productTemplate);
  const transformedSizeOptions = transformSizeOptions(sizeOptions);
  const dispatch = useFitFeedbackFormDispatch();
  const sizePurchasedError = touched?.sneakersSizePurchased && errors?.sneakersSizePurchased;
  const sneakersFitError = touched?.sneakersFit && errors?.sneakersFit;
  const { sneakersSizePurchased, sneakersFit } = useFitFeedbackFormState();
  const { isMobile } = useWindowSize();

  const SNEAKERS_FIT = getTranslations(SNEAKERS_FIT_DATA);
  const handleOnChange = (type) => (e) => {
    const { value, name } = e.target;
    dispatch({ type, payload: value });
    setFieldValue(name, value);
  };

  return (
    <FitFeedbackFormSneakers.Wrapper>
      <QuestionForm
        checkboxClassName="checkboxGrid"
        checked={sneakersFit}
        className="questionText"
        data-qa="fit_feedback"
        error={sneakersFitError}
        gridStyle={fitGridStyle}
        isGrid
        name="sneakersFit"
        onChange={handleOnChange(FitFeedbackFormActions.setSneakersFit)}
        options={SNEAKERS_FIT}
        question={t(`${TP}.sneakersFit`, 'How did the shoe end up fitting?')}
        type={QuestionFormType.CHECKBOX_RADIO}
      />

      <QuestionForm
        checked={sneakersSizePurchased}
        choiceClassName="sizeGrid"
        className="questionText"
        data-qa="fit_feedback"
        error={sizePurchasedError}
        gridStyle={sizeGridStyle(isMobile ? 6 : 5)}
        name="sneakersSizePurchased"
        onChange={handleOnChange(FitFeedbackFormActions.setSneakersSizePurchased)}
        options={transformedSizeOptions}
        withBorders
        question={t(`${TP}.sneakersSizePurchased`, 'What size shoe did you purchase?')}
      />
    </FitFeedbackFormSneakers.Wrapper>
  );
};

FitFeedbackFormSneakers.Wrapper = styled.div`
  margin: 16px 0 -40px 0;

  .questionText {
    ${variableFontMedium({ size: '12.5px' })}

    legend {
      ${variableFontMedium({ size: '12.5px' })}
      @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
        ${variableFontMedium({ size: '12.5px' })}
      }
    }
  }

  .checkboxGrid {
    ${variableFont({ size: '12px', height: '12px', fontWeight: '400' })};

    @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
      ${variableFont({ size: '12.5' })}
    }
  }

  .sizeGrid {
    height: 47px;
    width: 47px;
    ${variableFont({ height: '12px', size: '12px', fontWeight: '400' })}
    border-radius: ${({ theme }) => theme.borderRadii.button};

    @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
      ${variableFont({ size: '12.5', fontWeight: '400' })}
    }
  }
`;
